import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { baseQueryWithAccount } from "@/redux/baseQuery";
import { createSearchParams } from "@/utils";
import { transformAccess, transformAccessList } from "./helpers";
import { AccessLink, GetAccessListParams, GetAccessListRawResponse, RawAccess, RequestAccessParams, RequestAccessResponse } from "./types";

/**
 * Arg for the single-link mutations. A bare `id` targets the active (logged-in) account —
 * used by the client portal and the EA "My Clients" tab. `{ id, accountId }` targets a managed
 * client (an operator acting on `:clientId`) so `baseQueryWithAccount` scopes the URL to them.
 */
type AccessMutationArg = string | { id: string; accountId?: string };
const accessId = (arg: AccessMutationArg) => (typeof arg === "string" ? arg : arg.id);
const accessAccountId = (arg: AccessMutationArg) => (typeof arg === "string" ? undefined : arg.accountId);

/**
 * Page size for any view that renders the whole access list rather than paging through it.
 * The route (`module/AccountManagement/config/api-configs/access.php`) pages at 12 by default and
 * whitelists no all-results parameter, so a caller with no pager of its own must ask for a large
 * page and disclose the remainder — otherwise everything past the twelfth row is silently dropped
 * behind a scrollbar. 500 is the size the booking-history join has asked this route for since
 * DEV-1296, so every whole-list caller now shares one number rather than picking its own.
 */
export const ACCESS_LIST_PAGE_SIZE = "500";

/**
 * Account-management access API. NestJS proxies `/account-management/*` to PHP.
 * `baseQueryWithAccount()` builds `/v3/account-management/{me}/...` where `{me}` is the active
 * (logged-in) account by default, or the `accountId` passed in the args — e.g. an operator
 * viewing/removing a managed client's assistants from the Manage-Client Settings › Assistants tab.
 */
export const assistantsApi = createApi({
  reducerPath: "assistantsApi",
  baseQuery: baseQueryWithAccount(),
  tagTypes: ["Access"],
  endpoints: (builder) => ({
    getAccessList: builder.query<AccessLink[], GetAccessListParams & { accountId?: string }>({
      // No leading slash: baseQueryWithAccount("") already appends the trailing "/" after {me}.
      // `accountId` (when set) scopes the URL to a managed client and is kept out of the query string.
      query: ({ accountId, ...params }) => ({ url: `access?${createSearchParams(params).toString()}`, accountId }),
      transformResponse: (res: GetAccessListRawResponse) => transformAccessList(res),
      providesTags: ["Access"],
    }),
    /**
     * Same route as `getAccessList`, but keeps the HAL envelope's `total_items` alongside the rows
     * so a caller can tell a full page from a truncated one. The access route pages at 12 by
     * default (access.php) and offers no all-results escape hatch, so any caller rendering the
     * whole list must both ask for a large page and disclose when the server holds more.
     */
    getAccessListPage: builder.query<{ items: AccessLink[]; total: number }, GetAccessListParams & { accountId?: string }>({
      query: ({ accountId, ...params }) => ({ url: `access?${createSearchParams(params).toString()}`, accountId }),
      transformResponse: (res: GetAccessListRawResponse) => ({ items: transformAccessList(res), total: res?.total_items ?? 0 }),
      providesTags: ["Access"],
    }),
    getAccess: builder.query<AccessLink, string>({
      query: (id) => `access/${id}`,
      transformResponse: (res: RawAccess) => transformAccess(res),
      providesTags: ["Access"],
    }),
    // EA-only. 403 for non-EA accounts. Returns a per-email batch result.
    requestAccess: builder.mutation<RequestAccessResponse, RequestAccessParams>({
      query: (body) => ({ url: "access-request", method: "POST", body }),
      invalidatesTags: ["Access"],
    }),
    // Owner (client) approves an assistant's pending request. Client-consent only — not exposed to operators.
    approveAccess: builder.mutation<void, AccessMutationArg>({
      query: (arg) => ({ url: `access/${accessId(arg)}/approve`, method: "POST", accountId: accessAccountId(arg) }),
      invalidatesTags: ["Access"],
    }),
    // Owner (client) declines an assistant's pending request. Client-consent only — not exposed to operators.
    declineAccess: builder.mutation<void, AccessMutationArg>({
      query: (arg) => ({ url: `access/${accessId(arg)}/decline`, method: "POST", accountId: accessAccountId(arg) }),
      invalidatesTags: ["Access"],
    }),
    // Accessor re-notifies the owner of a still-pending invite/request. Accessor-only on the backend.
    resendAccess: builder.mutation<void, AccessMutationArg>({
      query: (arg) => ({ url: `access/${accessId(arg)}/resend`, method: "POST", accountId: accessAccountId(arg) }),
      invalidatesTags: ["Access"],
    }),
    // Either party removes an established (or pending) link. Operators may remove for a managed client.
    removeAccess: builder.mutation<void, AccessMutationArg>({
      query: (arg) => ({ url: `access/${accessId(arg)}`, method: "DELETE", accountId: accessAccountId(arg) }),
      invalidatesTags: ["Access"],
    }),
  }),
});

export const {
  useGetAccessListQuery,
  useGetAccessListPageQuery,
  useGetAccessQuery,
  useRequestAccessMutation,
  useApproveAccessMutation,
  useDeclineAccessMutation,
  useResendAccessMutation,
  useRemoveAccessMutation,
} = assistantsApi;
